// LTI 1.3 Type Definitions

export interface PlatformRegistration {
  name: string;
  platformId: string; // issuer URL (e.g., "https://canvas.instructure.com")
  clientId?: string; // optional — if omitted, accepts any clientId from this platform
  authEndpoint: string;
  tokenEndpoint?: string; // optional when deriveTokenEndpoint is true
  jwksEndpoint: string;
  deploymentIds?: string[];
  /** When true, tokenEndpoint is derived from the AGS lineitem URL at launch time.
   * Use for platforms like Canvas Cloud where all instances share the same issuer
   * but have instance-specific token endpoints. */
  deriveTokenEndpoint?: boolean;
}

export interface LtiSessionPayload {
  iss: string; // platform issuer
  sub: string; // LTI user ID
  name?: string; // LTI user display name
  clientId: string;
  deploymentId: string;
  roles: string[]; // LTI role URIs
  contextId: string; // course ID
  contextTitle: string;
  resourceLinkId: string;
  quizSetId?: string; // from custom claims
  entryMapHashId?: string; // from custom claims
  agsLineitemUrl?: string; // AGS lineitem endpoint
  platformTokenEndpoint: string;
  deepLinkingSettings?: LtiDeepLinkingSettings;
  gradeSubmitted?: boolean; // Set after first grade submission to prevent re-submission
  csrf: string; // CSRF double-submit token; compared against X-CSRF-Token header on state-changing requests
  exp: number;
}

export interface LtiDeepLinkingSettings {
  deep_link_return_url: string;
  accept_types: string[];
  accept_presentation_document_targets: string[];
  accept_multiple: boolean;
  data: string; // opaque platform data, echo back verbatim
}

export interface ContentItem {
  type: 'ltiResourceLink';
  url: string;
  title: string;
  lineItem?: {
    scoreMaximum: number;
    label: string;
    resourceId: string;
  };
  custom?: Record<string, string>;
}

export interface AgsScore {
  userId: string;
  scoreGiven: number;
  scoreMaximum: number;
  activityProgress:
    | 'Initialized'
    | 'Started'
    | 'InProgress'
    | 'Submitted'
    | 'Completed';
  gradingProgress:
    | 'FullyGraded'
    | 'Pending'
    | 'PendingManual'
    | 'Failed'
    | 'NotReady';
  timestamp: string;
  comment?: string;
}

export interface AgsResult {
  id: string;
  userId: string;
  scoreOf: string;
  resultScore: number;
  resultMaximum: number;
  comment?: string;
}

// LTI claim namespaces
export const LTI_CLAIMS = {
  MESSAGE_TYPE: 'https://purl.imsglobal.org/spec/lti/claim/message_type',
  VERSION: 'https://purl.imsglobal.org/spec/lti/claim/version',
  DEPLOYMENT_ID: 'https://purl.imsglobal.org/spec/lti/claim/deployment_id',
  TARGET_LINK_URI: 'https://purl.imsglobal.org/spec/lti/claim/target_link_uri',
  RESOURCE_LINK: 'https://purl.imsglobal.org/spec/lti/claim/resource_link',
  ROLES: 'https://purl.imsglobal.org/spec/lti/claim/roles',
  CONTEXT: 'https://purl.imsglobal.org/spec/lti/claim/context',
  CUSTOM: 'https://purl.imsglobal.org/spec/lti/claim/custom',
  AGS_ENDPOINT: 'https://purl.imsglobal.org/spec/lti-ags/claim/endpoint',
  DEEP_LINKING_SETTINGS:
    'https://purl.imsglobal.org/spec/lti-dl/claim/deep_linking_settings',
  DEEP_LINKING_DATA: 'https://purl.imsglobal.org/spec/lti-dl/claim/data',
  DEEP_LINKING_CONTENT_ITEMS:
    'https://purl.imsglobal.org/spec/lti-dl/claim/content_items',
  FOR_USER: 'https://purl.imsglobal.org/spec/lti/claim/for_user',
} as const;

export const LTI_MESSAGE_TYPES = {
  RESOURCE_LINK: 'LtiResourceLinkRequest',
  DEEP_LINKING: 'LtiDeepLinkingRequest',
  DEEP_LINKING_RESPONSE: 'LtiDeepLinkingResponse',
} as const;

export const LTI_ROLES = {
  INSTRUCTOR: 'http://purl.imsglobal.org/vocab/lis/v2/membership#Instructor',
  LEARNER: 'http://purl.imsglobal.org/vocab/lis/v2/membership#Learner',
  ADMIN:
    'http://purl.imsglobal.org/vocab/lis/v2/institution/person#Administrator',
  TEACHING_ASSISTANT:
    'http://purl.imsglobal.org/vocab/lis/v2/membership/Instructor#TeachingAssistant',
} as const;

const INSTRUCTOR_ROLE_PATTERNS = [
  LTI_ROLES.INSTRUCTOR,
  LTI_ROLES.ADMIN,
  LTI_ROLES.TEACHING_ASSISTANT,
] as const;

/** Check if any of the given LTI role URIs indicate instructor-level access. */
export function isInstructorRole(roles: string[]): boolean {
  return roles.some(role =>
    (INSTRUCTOR_ROLE_PATTERNS as readonly string[]).includes(role),
  );
}

export const AGS_SCOPES = {
  LINEITEM: 'https://purl.imsglobal.org/spec/lti-ags/scope/lineitem',
  SCORE: 'https://purl.imsglobal.org/spec/lti-ags/scope/score',
  RESULT_READONLY:
    'https://purl.imsglobal.org/spec/lti-ags/scope/result.readonly',
} as const;
